import { useEffect, useRef } from 'react';
import type { PagesRefs } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.types';
import { isScrollAnchoringSupported, SCROLL_AWAY_STOP_PX } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/scrollAnchoring';
import { useHistory } from '@shared/core';

type KeepPageScrollPositionProps = {
  pagesRef: PagesRefs[];
  container: HTMLDivElement | null;
  targetPage?: HTMLDivElement | null;
  /**
   * Whether `targetPage` is the first section. Compared by page id upstream, not by ref identity:
   * a section remount swaps the DOM node while the id stays put, and an identity check would then
   * silently start pinning the first page again.
   */
  isTargetFirstPage?: boolean;
};

export const useKeepPageScrollPosition = ({ pagesRef, container, targetPage, isTargetFirstPage }: KeepPageScrollPositionProps) => {
  const history = useHistory();

  // Keep pagesRef in a ref so the observer callback always reads the latest
  // array without the effect needing to re-run (and destroy the observer).
  const pagesRefRef = useRef(pagesRef);
  pagesRefRef.current = pagesRef;

  // Track page heights across observer callbacks in a ref that persists
  // across effect re-runs so height deltas are never lost.
  const heightMapRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!pagesRef?.length || !targetPage || !container) {
      return;
    }

    // Safari-only fallback. Where scroll anchoring exists it compensates inside the layout
    // pass itself; this hook must measure growth first, and that measurement forces the
    // layout in which Chrome records the shift — so running it there adds CLS without
    // adding correctness. Candidacy is deterministic (target-only), so anchoring cannot
    // pin the wrong node. See scrollAnchoring.ts. PRNT-4799.
    if (isScrollAnchoringSupported()) {
      return;
    }

    let observer: MutationObserver;

    const keepPageScrollPositionWhenDOMChanges = () => {
      // Track absolute-Y and scrollTop across callbacks so the correction
      // formula only compensates for *uncorrected* content growth.
      //
      // absoluteY = viewportTop + scrollTop is invariant under scroll — it
      // only changes when DOM content above the target grows or shrinks.
      // scrollTop changes from user scroll, ResizeObserver corrections, OR
      // our own prior adjustments.
      //
      // correction = contentShift − scrollChange gives the growth that hasn't
      // been compensated yet — so if another mechanism (the ResizeObserver in
      // useScrollToPageWhenUrlChanges) already scrolled to account for the
      // growth, scrollChange absorbs it and correction ≈ 0.
      // Container-relative, never viewport-relative: `scrollTop` is measured from the container's own
      // top, and with a website banner active GuestSiteBody pushes that 68px (mobile) / 77px (desktop)
      // below the viewport. The gap cancels in differences like `contentShift`, but it corrupts every
      // comparison against `scrollTop` — the reached test below and the visible-area test in the walk,
      // both of which would place the guest a banner's height further up the page than they are. Also
      // makes the readings immune to the container itself moving, e.g. a banner collapsing. PRNT-4799.
      const containerTopOf = () => container.getBoundingClientRect().top;
      const offsetInContainerOf = (el: HTMLElement) => el.getBoundingClientRect().top - containerTopOf() + container.scrollTop;

      let lastAbsoluteY = targetPage ? offsetInContainerOf(targetPage) : 0;
      let lastScrollTop = container?.scrollTop ?? 0;

      // Seed the height map for any pages we haven't seen yet
      for (const { page, ref } of pagesRefRef.current) {
        if (ref && !heightMapRef.current.has(page.id)) {
          heightMapRef.current.set(page.id, ref.offsetHeight);
        }
      }

      // First page only: its landing was the document TOP, not the section's current position (see
      // readDestinationY, which returns 0 for it), so while the guest is still ABOVE that section —
      // reading the standalone website header — a compensation would scroll them past that header.
      // Traced at scrollTop 0 on the designer preview, and reported by mobile-Safari guests, whose
      // header photo/video only reaches its real height once the media loads.
      //
      // Positional rather than session-wide: `targetPage` follows the URL, and scroll-driven URL
      // updates only `replace`, so after a root landing the first section stays the target however
      // far the guest scrolls. Once reached, compensating is exactly what holds the section they are
      // reading still.
      //
      // Judged against `lastAbsoluteY`, the target's document-Y from before this mutation, and never
      // against its fresh rect: growth larger than the distance a guest had just crossed the target
      // pushes its top back below the viewport top, which is indistinguishable from a guest who
      // never left the header. Recomputed per callback and never remembered — natural scrolling in
      // either direction is invisible to this hook, so a stored verdict outlives the position it
      // described.
      //
      // A guard, not the operative defence: `growthAbove` counts PAGE height growth above the
      // viewport and the header is not a page, so with the first section as the target the clamp is
      // already 0 while the guest sits above it. Kept because it states the rule the drag violates.
      // PRNT-4799.

      const updatePageScroll = () => {
        // A section remount replaces the node; this callback runs at the microtask checkpoint,
        // BEFORE the effect cleanup disconnects the observer, and a detached rect reads all zeros —
        // one garbage contentShift. Same guard as startPositionKeeper's. PRNT-4799.
        if (!targetPage?.isConnected) {
          observer.disconnect();
          return;
        }
        const currentPages = pagesRefRef.current;

        // --- Compute how much correction is still needed ---
        const currentAbsoluteY = targetPage ? offsetInContainerOf(targetPage) : 0;
        const currentScrollTop = container?.scrollTop ?? 0;

        const contentShift = currentAbsoluteY - lastAbsoluteY;
        const scrollChange = currentScrollTop - lastScrollTop;
        const correction = contentShift - scrollChange;

        // `currentScrollTop > 1` as well as the comparison: before the header renders the target sits
        // at the document top, so `lastAbsoluteY` is ~0 and the comparison alone would call a guest
        // at 0 "reached" and let the header's arrival scroll them down by its full height.
        // Same rule as the keeper's: until real content (> the shared bar) stands above the target,
        // reaching it demands a deliberate scroll past that bar, not past the 1px epsilon — a pre-
        // or partially-rendered header would otherwise let a nudge count as reached.
        const reachBarPx = lastAbsoluteY > SCROLL_AWAY_STOP_PX ? 1 : SCROLL_AWAY_STOP_PX;
        const hasReachedTarget = !isTargetFirstPage || (currentScrollTop > reachBarPx && currentScrollTop >= lastAbsoluteY - 1);

        // --- Walk pages to update height map and check relevance ---
        let growthAbove = 0;

        currentPages.forEach(({ ref: pageRef, page }) => {
          if (!pageRef) return;

          const trackedHeight = heightMapRef.current.get(page.id) ?? pageRef.offsetHeight;
          const newPageHeight = pageRef.offsetHeight;
          const heightDifference = newPageHeight - trackedHeight;

          // Always update tracked height
          heightMapRef.current.set(page.id, newPageHeight);

          if (heightDifference <= 0) return;

          // Against the container's top edge, which is where the visible area actually begins — the
          // same coordinate-space correction as above. Algebraically getElementPosition's own test
          // with the container offset removed (it measured `rect.top + scrollTop` against `scrollTop`,
          // i.e. the same thing whenever the container starts at the viewport top — every event
          // without a website banner), so the offsetHeight it used is kept rather than rect.bottom.
          const topWithinVisibleArea = pageRef.getBoundingClientRect().top - containerTopOf();
          const isPageBeforeVisibleArea = topWithinVisibleArea + newPageHeight <= 0;
          const isPagePartiallyVisibleArea = topWithinVisibleArea <= 0 && topWithinVisibleArea + newPageHeight >= 0;

          // Update the scroll position if the changed page is before the visible screen
          if (isPageBeforeVisibleArea || isPagePartiallyVisibleArea) {
            growthAbove += heightDifference;
          }
        });

        // Only adjust if there's uncorrected growth above the viewport.
        // A threshold of 1px avoids sub-pixel noise.
        //
        // Clamp to the growth actually observed: `scrollChange` can't distinguish user
        // scroll from compensating scroll, and user scroll accumulated between callbacks
        // would otherwise inflate the correction into a yank.
        const boundedCorrection = Math.min(correction, growthAbove);
        if (growthAbove > 0 && boundedCorrection > 1 && hasReachedTarget) {
          container.scrollTop = currentScrollTop + boundedCorrection;
        }

        // Re-anchor for the next callback.
        lastAbsoluteY = targetPage ? offsetInContainerOf(targetPage) : 0;
        lastScrollTop = container?.scrollTop ?? 0;
      };

      // Listen for container's DOM changes to update the page scroll position base on pages height changes.
      // childList-only: content growth that needs compensation comes from nodes mounting (widget
      // data arriving, lazy chunks rendering). Observing attributes fired this on every React
      // class/style commit — a forced layout per commit across all page sections — and the
      // accommodations note More/Less toggle (class-driven, no childList change) now simply never
      // triggers the observer, which preserves the old skip behavior for it.
      //
      // SYNCHRONOUS, never debounced/deferred: MutationObserver callbacks run at the microtask
      // checkpoint, BEFORE the frame that contains the mutation paints — so a scrollTop
      // compensation applied here is invisible and costs no layout shift (same pre-paint slot
      // native scroll anchoring uses). The previous debounce(20, maxWait 40) pushed the
      // correction into a later task, after the displaced frame had painted — every correction
      // was a counted layout shift (measured 1.13 CLS on a mobile Aloha deep link, the
      // corrections chasing scrollHeight 7k → 26k). The measurement cost this rehabilitates is
      // one offsetHeight read per section per mutation batch, only while widgets mount.
      // PRNT-4799.
      observer = new MutationObserver(updatePageScroll);
      observer.observe(container, { childList: true, subtree: true });
    };

    keepPageScrollPositionWhenDOMChanges();

    return () => {
      observer.disconnect();
    };
    // container and targetPage control observer lifecycle.
    // pagesRef is read via ref inside the callback — not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, targetPage, isTargetFirstPage, history.location.pathname]);
};
